// Global Instructions Rule Applied!
import { useState, useEffect, useCallback } from 'react'
import { searchFundraisingForPopup } from '../../app_modules/fundraising/services/fundraisingService.ts'
import { getAccessToken } from '../auth/authService'
import { store } from '../../state_manager/store.ts'

/** @type {import('../../state_manager/data_models/select_list_entry.ts').SelectListEntry[]} */
const NO_PROSPECTS = []

export const useProspectSearch = (debounceMs = 300) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [results, setResults] = useState(NO_PROSPECTS)
  const [searchTerm, setSearchTerm] = useState('')
  const [hasSearched, setHasSearched] = useState(false)

  const searchProspects = useCallback(
    async (query) => {
      const trimmedQuery = query?.trim() || ''
      setSearchTerm(trimmedQuery)

      if (!trimmedQuery || trimmedQuery.length < 2) {
        setResults([])
        setLoading(false)
        setError(null)
        setHasSearched(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        const currentToken = getAccessToken(store.getState().auth)
        const response = await searchFundraisingForPopup(trimmedQuery, 20, currentToken)

        if (response.error) {
          throw new Error(response.error)
        }

        if (trimmedQuery === searchTerm || searchTerm === '') {
          const formattedResults = (response.data || []).map((prospect) => ({
            label: prospect.accounts__name || '',
            value: prospect.prospects__id,
            name: prospect.accounts__name || ''
          }))
          setResults(formattedResults)
          setHasSearched(true)
        }
      } catch (error) {
        console.error('Error searching prospects:', error)
        setError(error.message || 'An unexpected error occurred while searching prospects')
        setResults([])
        setHasSearched(true)
      } finally {
        setLoading(false)
      }
    },
    [searchTerm]
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchProspects(searchTerm)
      }
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchTerm, searchProspects, debounceMs])

  const clearResults = useCallback(() => {
    setResults([])
    setSearchTerm('')
    setError(null)
    setLoading(false)
    setHasSearched(false)
  }, [])

  const triggerSearch = useCallback(
    (query) => {
      setSearchTerm(query)
      searchProspects(query)
    },
    [searchProspects]
  )

  return {
    searchProspects: triggerSearch,
    loading,
    error,
    results,
    clearResults,
    hasSearched
  }
}
